// When importing this file from a .js file, always include the .ts extension in the import statement.
// Example: import { useMeetingNotes } from '../hooks/useMeetingNotes.ts';
// This prevents module resolution errors in mixed JS/TS codebases.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface MeetingNote {
  id: number;
  title: string;
  date: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface CreateMeetingNoteData {
  title: string;
  date: string;
  content: string;
}

export interface UpdateMeetingNoteData {
  title: string;
  date: string;
  content: string;
}

// Get meeting notes
export const useMeetingNotes = () => {
  return useQuery({
    queryKey: ['meeting-notes'],
    queryFn: async () => {
      const response = await api.get('/meeting-notes');
      return response.data;
    },
  });
};

// Get single meeting note
export const useMeetingNote = (id: number) => {
  return useQuery({
    queryKey: ['meeting-notes', id],
    queryFn: async () => {
      const response = await api.get(`/meeting-notes/${id}`);
      return response.data.data as MeetingNote;
    },
    enabled: !!id,
  });
};

// Create meeting note
export const useCreateMeetingNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMeetingNoteData) => {
      const response = await api.post('/meeting-notes', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting-notes'] });
    },
  });
};

// Update meeting note
export const useUpdateMeetingNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMeetingNoteData }) => {
      const response = await api.put(`/meeting-notes/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['meeting-notes'] });
      queryClient.invalidateQueries({ queryKey: ['meeting-notes', id] });
    },
  });
};

// Delete meeting note
export const useDeleteMeetingNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/meeting-notes/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting-notes'] });
    },
  });
}; 